import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Phone, User, FileText } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function SupportPage() {
  const [formData, setFormData] = useState({
    order_id: '',
    name: '',
    email: '',
    phone: '',
    issue: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/support/tickets', formData);
      toast.success('Support ticket submitted! We will contact you soon.');
      setFormData({
        order_id: '',
        name: '',
        email: '',
        phone: '',
        issue: ''
      });
    } catch (error) {
      toast.error('Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-12" data-testid="support-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1a3a5c] mb-4" data-testid="support-title">
            Customer Support
          </h1>
          <p className="text-gray-600 text-lg">
            Having an issue with your order? We're here to help!
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-8 rounded-lg border border-gray-200"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order ID (Optional)
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.order_id}
                  onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                  placeholder="Enter your order tracking ID"
                  data-testid="order-id-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                  placeholder="Enter your name"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                  placeholder="Enter your email"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                  placeholder="Enter your phone number"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe Your Issue
              </label>
              <textarea
                value={formData.issue}
                onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                rows="6"
                placeholder="Please describe your issue in detail..."
                required
                data-testid="issue-textarea"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1a3a5c] text-white py-3 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50"
              data-testid="submit-support-button"
            >
              <Send className="h-5 w-5" />
              <span>{submitting ? 'Submitting...' : 'Submit Ticket'}</span>
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 bg-[#FDFBF7] p-6 rounded-lg border border-[#D4AF37]/20"
        >
          <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4">Contact Information</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Phone className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-gray-700">+91 98765 43210</span>
            </div>
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-gray-700">support@marissa.in</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Our support team is available Monday to Saturday, 10 AM to 6 PM IST
          </p>
        </motion.div>
      </div>
    </div>
  );
}